// panelCanvasGeom.ts — Pure geometry + snap/resize helpers for PanelCanvas2D
// Extracted from PanelCanvas2D.tsx to enable unit testing. No React dependencies.

import type { PanelData, MpVar } from '../../types/panelvar';

// ── Interaction constants ─────────────────────────────────────────────────────
export const SNAP      = 6;
export const SNAP_FINE = 0.5;
export const HANDLE_SZ = 7;
export const MIN_DIM   = 6;

// ── Types shared with PanelCanvas2D's drag/resize state machine ───────────────
export interface DragState {
  entityId:  string;
  action:    'move' | 'resize';
  handle:    string;
  startSvgX: number;
  startSvgY: number;
  fine:      boolean;
}
export interface SelBounds { svgX: number; svgY: number; svgW: number; svgH: number }
export interface RszHandle { id: string; x: number; y: number; handle: string; cursor: string }

// ── Convert inches → feet-inches string w/ Unicode fractions ─────────────────
const FRACS = ['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'];
export function toFtIn(totalInches: number): string {
  const neg = totalInches < 0;
  const eighths = Math.round(Math.abs(totalInches) * 8);
  const ft = Math.floor(eighths / 96);
  const rem = eighths % 96;
  const wh = Math.floor(rem / 8);
  const fr = rem % 8;
  const sign = neg ? '-' : '';
  if (ft === 0) return `${sign}${wh}${FRACS[fr]}"`;
  if (wh === 0 && fr === 0) return `${sign}${ft}'-0"`;
  return `${sign}${ft}'-${wh}${FRACS[fr]}"`;
}

// ── Snap a value to the nearest multiple of step (default SNAP=6) ─────────────
export function snapTo(v: number, step = SNAP) { return Math.round(v / step) * step; }

// ── Deep-clone an array of slot records (preserves `data` sub-object) ─────────
export function cloneSlots<T>(arr: T[]): T[] {
  return arr.map(s => ({ ...(s as object), data: { ...((s as any).data) } }) as T);
}

// ── Axis-aligned rectangle overlap test (exclusive on edges) ──────────────────
export function rectsOverlap(
  ax: number, ay: number, aw: number, ah: number,
  bx: number, by: number, bw: number, bh: number,
): boolean {
  return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
}

// ── Apply a move delta to a slot entity; returns updated array or null ────────
export function applyMove(
  panel: PanelData, entityId: string, dpx: number, dpy: number, fine: boolean,
): { slotKey: string; newArr: unknown[] } | null {
  if (entityId === 'panel-body') return null;
  const m = entityId.match(/^(\w+var)-(\d+)$/);
  if (!m) return null;
  const slotKey = m[1] as keyof PanelData;
  const idx = parseInt(m[2]);
  const step = fine ? SNAP_FINE : SNAP;
  const arr = panel[slotKey] as unknown as Array<{ enabled: boolean; data: Record<string, number> }>;
  if (!Array.isArray(arr) || idx >= arr.length || !arr[idx].enabled) return null;
  const newArr = cloneSlots(arr as object[]) as typeof arr;
  const d = newArr[idx].data;
  if      (slotKey === 'fhvar') { d.y = snapTo(d.y + dpy, step); }
  else if (slotKey === 'fvvar') { d.x = snapTo(d.x + dpx, step); }
  else { d.x = snapTo(d.x + dpx, step); d.y = snapTo(d.y + dpy, step); }
  return { slotKey, newArr };
}

// ── Shared rect-resize body for door/opening/feature slots ────────────────────
function applyResizeRect(
  d: Record<string, number>, handle: string, dpx: number, dpy: number, step: number, yIsBottom: boolean,
) {
  if (handle.includes('e')) { const dw = snapTo(dpx, step); d.width  = Math.max(MIN_DIM, d.width  + dw); d.x += dw / 2; }
  if (handle.includes('w')) { const dw = snapTo(dpx, step); d.width  = Math.max(MIN_DIM, d.width  - dw); d.x += dw / 2; }
  if (!yIsBottom) {
    if (handle.includes('n')) { const dh = snapTo(dpy, step); d.height = Math.max(MIN_DIM, d.height + dh); d.y += dh / 2; }
    if (handle.includes('s')) { const dh = snapTo(dpy, step); d.height = Math.max(MIN_DIM, d.height - dh); d.y += dh / 2; }
  } else {
    if (handle.includes('n')) { const dh = snapTo(dpy, step); d.height = Math.max(MIN_DIM, d.height + dh); }
    if (handle.includes('s')) { const dh = snapTo(dpy, step); d.height = Math.max(MIN_DIM, d.height - dh); d.y += dh; }
  }
}

// ── Apply a resize delta to either the panel body (mpvar) or a slot ───────────
export function applyResize(
  panel: PanelData, entityId: string, handle: string, dpx: number, dpy: number, fine: boolean,
): { slotKey: string; newArr: unknown[] } | { isMpVar: true; mpPatch: Partial<MpVar> } | null {
  const step = fine ? SNAP_FINE : SNAP;
  if (entityId === 'panel-body') {
    const mp = panel.mpvar;
    const mpPatch: Partial<MpVar> = {};
    if (handle.includes('e')) mpPatch.width  = Math.max(MIN_DIM, snapTo(mp.width  + dpx, step));
    if (handle.includes('n')) mpPatch.height = Math.max(MIN_DIM, snapTo(mp.height + dpy, step));
    return Object.keys(mpPatch).length > 0 ? { isMpVar: true, mpPatch } : null;
  }
  const m = entityId.match(/^(\w+var)-(\d+)$/);
  if (!m) return null;
  const slotKey = m[1] as keyof PanelData;
  const idx = parseInt(m[2]);
  const arr = panel[slotKey] as unknown as Array<{ enabled: boolean; data: Record<string, number> }>;
  if (!Array.isArray(arr) || idx >= arr.length || !arr[idx].enabled) return null;
  const newArr = cloneSlots(arr as object[]) as typeof arr;
  const d = newArr[idx].data;
  if      (slotKey === 'rbvar') { if (handle.includes('e')) d.diameter = Math.max(MIN_DIM, snapTo(d.diameter + dpx * 2, step)); }
  else if (slotKey === 'fhvar') {
    if (handle.includes('n')) { const dh = snapTo(dpy, step); d.height = Math.max(MIN_DIM, d.height + dh); }
    if (handle.includes('s')) { const dh = snapTo(dpy, step); d.height = Math.max(MIN_DIM, d.height - dh); d.y += dh; }
  } else if (slotKey === 'fvvar') {
    if (handle.includes('e')) { const dw = snapTo(dpx, step); d.width = Math.max(MIN_DIM, d.width + dw); d.x += dw / 2; }
    if (handle.includes('w')) { const dw = snapTo(dpx, step); d.width = Math.max(MIN_DIM, d.width - dw); d.x += dw / 2; }
  } else if (slotKey === 'drvar') { applyResizeRect(d, handle, dpx, dpy, step, true); }
  else                            { applyResizeRect(d, handle, dpx, dpy, step, false); }
  return { slotKey, newArr };
}

// ── Compute resize-handle positions for the currently-selected entity ─────────
export function getResizeHandles(entityId: string, sb: SelBounds): RszHandle[] {
  const { svgX, svgY, svgW, svgH } = sb;
  const cx = svgX + svgW / 2, cy = svgY + svgH / 2, re = svgX + svgW, bo = svgY + svgH;
  if (entityId === 'panel-body') return [
    { id: 'e',  x: re, y: cy,   handle: 'e',  cursor: 'ew-resize'   },
    { id: 'n',  x: cx, y: svgY, handle: 'n',  cursor: 'ns-resize'   },
    { id: 'ne', x: re, y: svgY, handle: 'ne', cursor: 'nesw-resize' },
  ];
  const mt = entityId.match(/^(\w+var)/);
  const st = mt ? mt[1] : '';
  if (!st || st === 'wcvar' || st === 'bpvar' || st === 'ppvar' || st === 'plvar') return [];
  if (st === 'rbvar') return [{ id: 'e', x: re, y: cy, handle: 'e', cursor: 'ew-resize' }];
  if (st === 'fhvar') return [
    { id: 'n', x: cx, y: svgY, handle: 'n', cursor: 'ns-resize' },
    { id: 's', x: cx, y: bo,   handle: 's', cursor: 'ns-resize' },
  ];
  if (st === 'fvvar') return [
    { id: 'e', x: re,   y: cy, handle: 'e', cursor: 'ew-resize' },
    { id: 'w', x: svgX, y: cy, handle: 'w', cursor: 'ew-resize' },
  ];
  return [
    { id: 'nw', x: svgX, y: svgY, handle: 'nw', cursor: 'nwse-resize'  },
    { id: 'n',  x: cx,   y: svgY, handle: 'n',  cursor: 'ns-resize'    },
    { id: 'ne', x: re,   y: svgY, handle: 'ne', cursor: 'nesw-resize'  },
    { id: 'e',  x: re,   y: cy,   handle: 'e',  cursor: 'ew-resize'    },
    { id: 'se', x: re,   y: bo,   handle: 'se', cursor: 'nwse-resize'  },
    { id: 's',  x: cx,   y: bo,   handle: 's',  cursor: 'ns-resize'    },
    { id: 'sw', x: svgX, y: bo,   handle: 'sw', cursor: 'nesw-resize'  },
    { id: 'w',  x: svgX, y: cy,   handle: 'w',  cursor: 'ew-resize'    },
  ];
}
